import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, MenuItem, Select, FormControl, InputLabel, Chip, Alert, Stack, CircularProgress } from '@mui/material';
import { Add as AddIcon, ListAltOutlined as EditIcon, DeleteOutlined as DeleteIcon, FilterListOutlined as FilterIcon } from '@mui/icons-material';
import RoleModal from '../components/RoleModal';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { UserAPI } from '../apis/UserAPI';
import { useAllApplicationsList, usePoliciesList, clearRolesCache } from '../components/ApplicationContext';
import { isAuthorized } from '../utils/utilities';
import authenticatedRoute from '../views/AuthenticatedRoute';
import { constants } from '../constants';
import { useThemeDetails } from '../components/ThemeDetailsContext';
// Helper function to validate role update requirements
const validateRoleForUpdate = (role) => {
    return !!(role.application_id &&
        role.role_key &&
        role.organization_id &&
        role.id);
};
// Helper function to normalize application_id to string array
const normalizeAppIds = (appId) => {
    if (!appId)
        return [];
    if (Array.isArray(appId))
        return appId.map(String);
    if (typeof appId === 'string' && appId.includes(',')) {
        return appId.split(',').map((id) => id.trim());
    }
    return [String(appId)];
};
// Helper function to format role date from metadata
const formatRoleDate = (metadata) => {
    const dateOptions = {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    };
    if (!metadata)
        return 'Unknown';
    if ('createdDate' in metadata &&
        typeof metadata.createdDate === 'string' &&
        metadata.createdDate) {
        return new Date(metadata.createdDate).toLocaleDateString('en-US', dateOptions);
    }
    if ('updatedDate' in metadata &&
        typeof metadata.updatedDate === 'string' &&
        metadata.updatedDate) {
        return new Date(metadata.updatedDate).toLocaleDateString('en-US', dateOptions);
    }
    return 'Unknown';
};
// Helper function to handle policy updates for existing roles
const handleExistingRolePolicyUpdates = async (roleData, selectedRole, policies) => {
    if (!roleData.policies || !selectedRole.id)
        return;
    const currentPoliciesResponse = await UserAPI.getRolePolicies(selectedRole.id);
    const currentPolicyNames = currentPoliciesResponse.resource.map((p) => p.name);
    // Find policies to add and remove
    const policiesToAdd = roleData.policies.filter((p) => !currentPolicyNames.includes(p));
    const policiesToRemove = currentPolicyNames.filter((p) => !roleData.policies.includes(p));
    // Add new policies
    if (policiesToAdd.length > 0) {
        const policyIdsToAdd = policiesToAdd
            .map((policyName) => policies.find((p) => p.name === policyName)?.id)
            .filter((id) => id !== undefined);
        if (policyIdsToAdd.length > 0) {
            await UserAPI.createRolePolicy({
                role_id: Number.parseInt(selectedRole.id),
                policy_id: policyIdsToAdd
            });
        }
    }
    // Remove deselected policies
    if (policiesToRemove.length > 0) {
        const policyIdsToRemove = policiesToRemove
            .map((policyName) => policies.find((p) => p.name === policyName)?.id)
            .filter((id) => id !== undefined);
        if (policyIdsToRemove.length > 0) {
            await UserAPI.removeSpecificPoliciesFromRole(selectedRole.id, policyIdsToRemove);
        }
    }
};
// Helper function to assign policies to new role
const assignPoliciesToNewRole = async (roleData, newRole, policies) => {
    if (!roleData.policies?.length || !newRole.id)
        return;
    const selectedPolicyIds = roleData.policies
        .map((policyName) => policies.find((p) => p.name === policyName)?.id)
        .filter((id) => id !== undefined);
    if (selectedPolicyIds.length > 0) {
        await UserAPI.createRolePolicy({
            role_id: Number.parseInt(newRole.id),
            policy_id: selectedPolicyIds
        });
        newRole.policies = roleData.policies;
    }
};
// Helper function to update existing role
const updateExistingRole = async (roleData, selectedRole, policies, loadInitialData) => {
    if (!validateRoleForUpdate(selectedRole)) {
        throw new Error('Missing required role information. Unable to update role.');
    }
    // Always send application_id as array (empty array for 'None' option)
    const appIds = normalizeAppIds(roleData.application_id);
    const updateRequest = {
        organization_id: Number.parseInt(selectedRole.organization_id || ''),
        application_id: appIds,
        role_name: roleData.name,
        role_key: roleData.name.toLowerCase().replaceAll(/\s+/g, '_')
    };
    await UserAPI.updateRole(selectedRole.id || '', updateRequest);
    // Handle policy updates
    try {
        await handleExistingRolePolicyUpdates(roleData, selectedRole, policies);
    }
    catch (err) {
        console.warn('Failed to update policies for role:', err);
    }
    await loadInitialData();
};
// Helper function to create new role
const createNewRole = async (roleData, selectedApplication, applications, policies) => {
    // Use all selected application IDs from roleData
    const selectedAppIds = normalizeAppIds(roleData.application_id);
    // Find the first selected application to get organization_id (or use default for 'None')
    const selectedApp = applications.find((app) => selectedAppIds.includes(String(app.id)));
    // Use organization_id from app if available, otherwise use first available app's org_id
    const organizationId = selectedApp?.organization_id ?? applications[0]?.organization_id;
    if (!organizationId) {
        throw new Error('No organization available. Please ensure at least one application exists.');
    }
    const createRequest = {
        organization_id: organizationId,
        application_id: selectedAppIds,
        role_name: roleData.name,
        role_key: roleData.name.toLowerCase().replaceAll(/\s+/g, '_')
    };
    const response = await UserAPI.createRole(createRequest);
    const newRole = {
        id: response.role_id || Date.now().toString(),
        name: response.role_name || roleData.name,
        application_id: normalizeAppIds(response.application_id).length > 0
            ? normalizeAppIds(response.application_id)
            : selectedAppIds,
        role_key: response.role_key ||
            roleData.name.toLowerCase().replaceAll(/\s+/g, '_'),
        policies: [],
        peopleAssigned: 0,
        dateAdded: new Date().toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        })
    };
    // Assign policies to new role
    try {
        await assignPoliciesToNewRole(roleData, newRole, policies);
    }
    catch (err) {
        console.warn('Failed to assign policies to new role:', err);
    }
    clearRolesCache();
    return newRole;
};
const RoleManagement = () => {
    const { stateImages } = useThemeDetails();
    const [roles, setRoles] = useState([]);
    const [applications, setApplications] = useState([]);
    const [policies, setPolicies] = useState([]);
    const [selectedApplication, setSelectedApplication] = useState('');
    const [dataLoading, setDataLoading] = useState(true);
    const [error, setError] = useState(null);
    // Modal and dialog state
    const [roleModalOpen, setRoleModalOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    // Load data on component mount
    useEffect(() => {
        loadInitialData();
    }, []);
    const getAllApplications = useAllApplicationsList();
    const getPolicies = usePoliciesList();
    const loadInitialData = async () => {
        setDataLoading(true);
        setError(null);
        try {
            // Load applications, roles with policies and counts, and policies in parallel
            const [apps, rolesWithDataResponse, policiesResource] = await Promise.all([
                getAllApplications(),
                UserAPI.getRolesWithPoliciesAndCounts(),
                getPolicies()
            ]);
            setApplications(apps.resource || apps);
            // Transform the aggregated role data to match the existing format
            const transformedRoles = rolesWithDataResponse.resource.map((role) => ({
                id: role.role_id,
                name: role.role_name,
                application_id: normalizeAppIds(role.application_id),
                organization_id: role.organization_id,
                role_key: role.role_key,
                policies: role.policies.map((policy) => policy.policy_name), // Extract policy names
                peopleAssigned: role.assignment_count,
                dateAdded: formatRoleDate(role.metadata)
            }));
            setRoles(transformedRoles);
            setPolicies(policiesResource);
            // Set default application filter to first application if available
            const appList = apps.resource || apps;
            if (Array.isArray(appList) && appList.length > 0) {
                setSelectedApplication(appList[0].name);
            }
        }
        catch (err) {
            console.error('Failed to load data:', err);
            setError(constants.role_mgmt_error_load_data);
            setRoles([]);
        }
        finally {
            setDataLoading(false);
        }
    };
    const handleAddRole = () => {
        if (!selectedApplication) {
            setError(constants.role_mgmt_select_app_before_create);
            return;
        }
        setSelectedRole(null);
        setRoleModalOpen(true);
    };
    const handleEditRole = (role) => {
        setSelectedRole(role);
        setRoleModalOpen(true);
    };
    const handleDeleteRole = (role) => {
        setRoleToDelete(role);
        setDeleteDialogOpen(true);
    };
    const handleSaveRole = async (roleData) => {
        setLoading(true);
        setError(null);
        try {
            if (selectedRole) {
                await updateExistingRole(roleData, selectedRole, policies, loadInitialData);
                setSuccessMessage(constants.role_mgmt_update_success);
            }
            else {
                const newRole = await createNewRole(roleData, selectedApplication, applications, policies);
                setRoles((prevRoles) => [...prevRoles, newRole]);
                setSuccessMessage(constants.role_mgmt_add_success);
            }
            setRoleModalOpen(false);
            setSelectedRole(null);
        }
        catch (err) {
            console.error('Error saving role:', err);
            setError(err instanceof Error
                ? err.message
                : constants.role_mgmt_error_save_role);
        }
        finally {
            setLoading(false);
        }
    };
    const confirmDeleteRole = async () => {
        if (roleToDelete) {
            setLoading(true);
            setError(null);
            try {
                if (!roleToDelete.id) {
                    throw new Error('Role ID is required for deletion');
                }
                // First remove all user/group assignments (principal-role)
                await UserAPI.deleteAllRoleAssignments(roleToDelete.id);
                // Then delete all role-policy mappings
                await UserAPI.deleteAllRolePolicies(roleToDelete.id);
                // Then delete the role itself
                await UserAPI.deleteRole(roleToDelete.id);
                setRoles((prevRoles) => prevRoles.filter((r) => r.id !== roleToDelete.id));
                clearRolesCache();
                setSuccessMessage(constants.role_mgmt_delete_success);
                setDeleteDialogOpen(false);
                setRoleToDelete(null);
            }
            catch (err) {
                console.error('Error deleting role:', err);
                setError(constants.role_mgmt_error_delete_role);
            }
            finally {
                setLoading(false);
            }
        }
    };
    // Check view permission
    if (!isAuthorized('RBAC:ViewRole')) {
        return (_jsx(Box, { sx: { p: 3, bgcolor: '#f8f8f8' }, children: _jsxs(Box, { sx: { textAlign: 'center', py: 8 }, children: [_jsx(Typography, { variant: "h6", color: "#666", gutterBottom: true, children: constants.role_mgmt_access_denied_title }), _jsx(Typography, { variant: "body1", color: "#888", children: constants.role_mgmt_access_denied_desc })] }) }));
    }
    return (_jsxs(Box, { sx: { p: 3, bgcolor: '#f8f8f8' }, children: [_jsxs(Paper, { sx: { p: 3, borderRadius: 2, boxShadow: 'none' }, children: [_jsxs(Box, { sx: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3
                        }, children: [_jsx(Typography, { variant: "body1", sx: { mb: 3 }, children: constants.role_mgmt_header_description }), _jsxs(Box, { sx: { display: 'flex', gap: 2, alignItems: 'center' }, children: [_jsxs(FormControl, { size: "small", sx: { minWidth: 200 }, children: [_jsx(InputLabel, { children: constants.policy_mgmt_application_label }), _jsxs(Select, { value: selectedApplication, onChange: (e) => setSelectedApplication(e.target.value), label: constants.policy_mgmt_application_label, disabled: dataLoading, children: [_jsx(MenuItem, { value: "none", children: constants.global_roles }), applications.map((app) => (_jsx(MenuItem, { value: app.name, children: app.name }, app.id || app.name)))] })] }), isAuthorized('RBAC:CreateRole') && (_jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: handleAddRole, sx: {
                                            textTransform: 'none',
                                            boxShadow: 'none'
                                        }, children: constants.role_mgmt_add_button }))] })] }), dataLoading && (_jsxs(Box, { sx: { textAlign: 'center', py: 4 }, children: [stateImages?.Loading ? (_jsx(Box, { component: "img", src: stateImages.Loading, alt: "Loading", sx: { width: 128, height: 128 } })) : (_jsx(CircularProgress, {})), _jsx(Typography, { variant: "body2", sx: { mt: 2 }, children: constants.role_mgmt_loading })] })), error && (_jsx(Alert, { severity: "error", sx: { mb: 3 }, children: error })), successMessage && (_jsx(Alert, { severity: "success", sx: { mb: 3 }, onClose: () => setSuccessMessage(''), children: successMessage })), !dataLoading && !error && (_jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { sx: {
                                                    color: '#212121',
                                                    borderBottom: '1px solid #ebebeb',
                                                    width: '270px'
                                                }, children: _jsxs(Stack, { direction: "row", alignItems: "center", gap: 1, children: [_jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: constants.role_mgmt_table_header_name }), _jsx(FilterIcon, { sx: {
                                                                fontSize: 18,
                                                                color: '#26890d'
                                                            } })] }) }), _jsx(TableCell, { sx: {
                                                    color: '#212121',
                                                    borderBottom: '1px solid #ebebeb'
                                                }, children: _jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: constants.role_mgmt_table_header_policies_assigned }) }), _jsx(TableCell, { sx: {
                                                    color: '#212121',
                                                    borderBottom: '1px solid #ebebeb'
                                                }, children: _jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: constants.role_mgmt_table_header_people_assigned }) }), _jsx(TableCell, { sx: {
                                                    color: '#212121',
                                                    borderBottom: '1px solid #ebebeb'
                                                }, children: _jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: constants.role_mgmt_table_header_date_added }) }), _jsx(TableCell, { sx: {
                                                    color: '#212121',
                                                    borderBottom: '1px solid #ebebeb',
                                                    width: '200px'
                                                }, children: _jsx(Typography, { variant: "body2", sx: { fontWeight: 600 }, children: constants.role_mgmt_table_header_actions }) })] }) }), _jsx(TableBody, { children: (() => {
                                        // Filter roles based on selected application
                                        const filteredRoles = roles.filter((role) => {
                                            if (!selectedApplication)
                                                return true;
                                            // Handle "None" option - show roles without application_id
                                            if (selectedApplication === 'none') {
                                                const appIds = normalizeAppIds(role.application_id);
                                                return appIds.every((id) => id === 'null' ||
                                                    id === 'undefined' ||
                                                    id === '');
                                            }
                                            // Find the application by name to get its ID
                                            const selectedApp = applications.find((app) => app.name ===
                                                selectedApplication);
                                            if (!selectedApp)
                                                return false;
                                            // Always treat application_id as array for filtering
                                            const appIds = normalizeAppIds(role.application_id);
                                            return appIds.includes(String(selectedApp.id));
                                        });
                                        if (filteredRoles.length === 0) {
                                            return (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 5, sx: {
                                                        textAlign: 'center',
                                                        py: 4
                                                    }, children: _jsx(Typography, { variant: "body2", color: "text.secondary", children: constants.role_mgmt_no_roles_found }) }) }));
                                        }
                                        return filteredRoles.map((role) => (_jsxs(TableRow, { children: [_jsx(TableCell, { sx: {
                                                        borderBottom: '1px solid #ebebeb',
                                                        py: 2
                                                    }, children: _jsx(Typography, { variant: "body1", sx: {
                                                            fontWeight: 600,
                                                            color: '#26890d',
                                                            textDecoration: 'underline',
                                                            cursor: isAuthorized('RBAC:UpdateRole')
                                                                ? 'pointer'
                                                                : 'default',
                                                            '&:hover': {
                                                                color: isAuthorized('RBAC:UpdateRole')
                                                                    ? '#1f6b0a'
                                                                    : '#26890d'
                                                            }
                                                        }, onClick: () => isAuthorized('RBAC:UpdateRole') &&
                                                            handleEditRole(role), children: role.name }) }), _jsx(TableCell, { sx: {
                                                        borderBottom: '1px solid #ebebeb',
                                                        py: 2
                                                    }, children: _jsx(Box, { sx: {
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            gap: 0.5
                                                        }, children: role.policies.length >
                                                            0 ? (role.policies.map((policy) => (_jsx(Chip, { label: policy, size: "small", sx: {
                                                                backgroundColor: '#f3f8eb',
                                                                color: '#1a5d09',
                                                                fontFamily: 'Open Sans',
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                height: 20
                                                            } }, policy)))) : (_jsx(Chip, { label: constants.role_mgmt_chip_no_policies, size: "small", sx: {
                                                                backgroundColor: '#ebebeb',
                                                                color: '#595959',
                                                                fontFamily: 'Open Sans',
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                height: 20
                                                            } })) }) }), _jsx(TableCell, { sx: {
                                                        borderBottom: '1px solid #ebebeb',
                                                        py: 2
                                                    }, children: _jsx(Typography, { variant: "body1", color: "#212121", children: role.peopleAssigned }) }), _jsx(TableCell, { sx: {
                                                        borderBottom: '1px solid #ebebeb',
                                                        py: 2
                                                    }, children: _jsx(Typography, { variant: "body1", color: "#212121", children: role.dateAdded }) }), _jsx(TableCell, { sx: {
                                                        borderBottom: '1px solid #ebebeb',
                                                        py: 2
                                                    }, children: _jsxs(Box, { sx: {
                                                            display: 'flex',
                                                            gap: 1
                                                        }, children: [isAuthorized('RBAC:UpdateRole') && (_jsx(IconButton, { onClick: () => handleEditRole(role), sx: {
                                                                    color: '#26890d',
                                                                    '&:hover': {
                                                                        bgcolor: 'rgba(38, 137, 13, 0.04)'
                                                                    }
                                                                }, children: _jsx(EditIcon, {}) })), isAuthorized('RBAC:DeleteRole') && (_jsx(IconButton, { onClick: () => handleDeleteRole(role), sx: {
                                                                    color: '#da291c',
                                                                    '&:hover': {
                                                                        bgcolor: 'rgba(218, 41, 28, 0.04)'
                                                                    }
                                                                }, children: _jsx(DeleteIcon, {}) }))] }) })] }, role.id)));
                                    })() })] }) }))] }), _jsx(RoleModal, { open: roleModalOpen, onClose: () => {
                    setRoleModalOpen(false);
                    setSelectedRole(null);
                }, onSave: handleSaveRole, role: selectedRole, loading: loading, applications: applications }), _jsx(ConfirmationDialog, { isOpen: deleteDialogOpen, setIsOpen: () => {
                    setDeleteDialogOpen(false);
                    setRoleToDelete(null);
                }, submitFunction: confirmDeleteRole, bodyText: constants.role_mgmt_delete_confirm_body(roleToDelete?.name ?? ''), submitButtonText: constants.delete, loading: loading })] }));
};
export default authenticatedRoute(RoleManagement);
